import React, { useState, useRef, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import placeholder from "../../assets/card_placeholder.png";
import fire from "../../assets/ic_fire.svg";
import diamond from "../../assets/ic_diamond.svg";
import DomainUtils from '../../services/DomainUtils';
import ApiService from '../../services/ApiService';
import './AnimeCardComponent.css';

// Memoize the component to prevent unnecessary re-renders
const AnimeCardComponent = memo(({ anime, isInteractive = false }) => {
    const [state, setState] = useState({
        isHovered: false,
        imageLoaded: false,
        isVisible: false,
        imageSrc: placeholder // Start with placeholder
    });
    const navigate = useNavigate();
    const cardRef = useRef();
    const expandTimerRef = useRef(null);
    const collapseLockRef = useRef(false);
    const wantCollapseRef = useRef(false);
    const unlockTimerRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const slideshowTimerRef = useRef(null);
    const slideshowActiveRef = useRef(false);
    const [slides, setSlides] = useState([]);
    const slidesRef = useRef([]);
    const indexRef = useRef(0);
    const [layerAUrl, setLayerAUrl] = useState(null);
    const [layerBUrl, setLayerBUrl] = useState(null);
    const [useLayerA, setUseLayerA] = useState(true);
    const activeLayerRef = useRef('A');
    const [slideshowReady, setSlideshowReady] = useState(false);

    const preloadImage = (src) => new Promise((resolve) => {
        const im = new Image();
        im.onload = () => resolve(true);
        im.onerror = () => resolve(false);
        im.src = src;
    });
    
    // Combine image loading and visibility detection in one effect
    useEffect(() => {
        // Setup Intersection Observer
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setState(prev => ({ ...prev, isVisible: true }));
                    observer.unobserve(cardRef.current);
                    
                    // Only load image when visible
                    if (anime && anime.id) {
                        const API_DOMAIN = window.location.hostname === 'localhost' 
                            ? 'https://api.animix.club' 
                            : `https://api.${DomainUtils.getDomain()}`;
                        
                        const animeImageUrl = `${API_DOMAIN}/anime/getThumb/${anime.id}_thumb.png`;
                        
                        const img = new Image();
                        img.src = animeImageUrl;
                        img.onload = () => {
                            setState(prev => ({ 
                                ...prev, 
                                imageSrc: animeImageUrl,
                                imageLoaded: true 
                            }));
                        };
                        img.onerror = () => {
                            setState(prev => ({ 
                                ...prev, 
                                imageLoaded: true 
                            }));
                        };
                    }
                }
            },
            { threshold: 0.01, rootMargin: '0px' } // Load only when actually on screen
        );

        if (cardRef.current) {
            observer.observe(cardRef.current);
        }

        return () => {
            if (cardRef.current) {
                observer.unobserve(cardRef.current);
            }
        };
    }, [anime]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
            if (slideshowTimerRef.current) clearTimeout(slideshowTimerRef.current);
            if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
        };
    }, []);

    // Start slideshow of series thumbs when expanded (wide card mode)
    useEffect(() => {
        const startSlideshow = async () => {
            try {
                // Avoid unnecessary work if not expanded
                if (!isExpanded) return;
                // Fetch series list for this anime
                const res = await ApiService.getSeries(anime.id);
                let list = [];
                if (Array.isArray(res?.data)) {
                    list = res.data;
                } else if (res?.data && typeof res.data === 'object') {
                    // API returns seasons map: { seasonNum: [series...] }
                    list = Object.values(res.data).flat();
                }
                if (list.length === 0) return;
                const API_DOMAIN = window.location.hostname === 'localhost'
                    ? 'https://api.animix.club'
                    : `https://api.${DomainUtils.getDomain()}`;
                // Build list of series thumbs (limit to a handful to keep it snappy)
                const imgs = list
                    .filter(s => s && s.privateId)
                    .slice(0, Math.min(8, list.length))
                    .map(s => `${API_DOMAIN}/anime/getThumb/${s.privateId}_thumb.png`);
                // Preload first two to ensure smooth start, others fire-and-forget
                await preloadImage(imgs[0]);
                if (!slideshowActiveRef.current) return;
                if (imgs[1]) preloadImage(imgs[1]);
                imgs.slice(2).forEach(u => { const im = new Image(); im.src = u; });
                setSlides(imgs);
                slidesRef.current = imgs;
                indexRef.current = 0;
                setLayerAUrl(imgs[0]);
                setLayerBUrl(null);
                setUseLayerA(true);
                activeLayerRef.current = 'A';
                // Small delay before first reveal so it doesn't pop immediately
                setSlideshowReady(false);
                const reveal = () => setSlideshowReady(true);
                const advance = async () => {
                    const arr = slidesRef.current;
                    if (!slideshowActiveRef.current || !arr || arr.length < 2) return;
                    const next = (indexRef.current + 1) % arr.length;
                    // Ensure next slide fully loaded before display
                    await preloadImage(arr[next]);
                    if (!slideshowActiveRef.current) return;
                    if (activeLayerRef.current === 'A') {
                        setLayerBUrl(arr[next]);
                        setUseLayerA(false);
                        activeLayerRef.current = 'B';
                    } else {
                        setLayerAUrl(arr[next]);
                        setUseLayerA(true);
                        activeLayerRef.current = 'A';
                    }
                    indexRef.current = next;
                    // schedule next frame after 4s on-screen
                    slideshowTimerRef.current = setTimeout(advance, 4000);
                };
                if (slideshowTimerRef.current) clearTimeout(slideshowTimerRef.current);
                // Reveal after a short delay and schedule the first advance
                setTimeout(reveal, 500);
                slideshowTimerRef.current = setTimeout(advance, 4000);
            } catch (e) {
                // silent fail - slideshow is optional
            }
        };

        if (isExpanded) {
            slideshowActiveRef.current = true;
            startSlideshow();
        } else {
            // Stop slideshow when collapsing
            slideshowActiveRef.current = false;
            if (slideshowTimerRef.current) {
                clearTimeout(slideshowTimerRef.current);
                slideshowTimerRef.current = null;
            }
            setLayerAUrl(null);
            setLayerBUrl(null);
            setUseLayerA(true);
            activeLayerRef.current = 'A';
            setSlideshowReady(false);
        }

        return () => {
            slideshowActiveRef.current = false;
            if (slideshowTimerRef.current) {
                clearTimeout(slideshowTimerRef.current);
                slideshowTimerRef.current = null;
            }
        };
    }, [isExpanded, anime.id]);

    // When expanded on the Home page, ensure the card is fully visible horizontally
    useEffect(() => {
        if (!isExpanded) return;
        if (typeof window === 'undefined') return;
        const path = window.location.pathname || '';
        if (path !== '/') return; // home page only

        const el = cardRef.current;
        if (!el) return;

        const doScrollIntoView = () => {
            const scroller = el.closest('.anime-line-container') || el.closest('.anime-line-container-padding');
            if (!scroller) return;

            // Minimal scroll to keep the card fully visible with margin, avoiding large jumps
            const rect = el.getBoundingClientRect();
            const srect = scroller.getBoundingClientRect();
            const margin = 24;
            let delta = 0;
            if (rect.right > srect.right - margin) {
                delta = rect.right - (srect.right - margin);
            } else if (rect.left < srect.left + margin) {
                delta = (rect.left - (srect.left + margin)); // negative value
            }
            if (Math.abs(delta) < 1) return;

            const target = Math.max(0, Math.min(scroller.scrollLeft + delta, scroller.scrollWidth - scroller.clientWidth));

            const prefersReduced = typeof window !== 'undefined' &&
                window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (prefersReduced) { scroller.scrollLeft = target; return; }

            // Manual smooth scroll for broad browser support (including Safari)
            const start = scroller.scrollLeft;
            const total = target - start;
            const duration = 420; // ms
            const startTime = performance.now();
            const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            const step = (now) => {
                const t = Math.min(1, (now - startTime) / duration);
                scroller.scrollLeft = start + total * easeInOutCubic(t);
                if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        };

        // Run after the width transition likely finished
        const t1 = setTimeout(doScrollIntoView, 520);

        // Also hook into transitionend to be precise on first expansion
        const onEnd = (ev) => {
            if (ev.target === el && ev.propertyName === 'width') {
                doScrollIntoView();
            }
        };
        el.addEventListener('transitionend', onEnd);

        return () => {
            clearTimeout(t1);
            el.removeEventListener('transitionend', onEnd);
        };
    }, [isExpanded]);

    const handleCardInteraction = (isEntering) => {
        if (!isInteractive) return;
        const isHome = typeof window !== 'undefined' && (window.location.pathname || '') === '/';
        // Always apply hover visuals everywhere
        setState(prev => ({ ...prev, isHovered: isEntering }));

        if (isEntering) {
            // Only expand on larger screens / pointer devices
            const isNarrow = window.innerWidth <= 720;
            if (isNarrow) return;
            // Only allow expansion on homepage
            if (!isHome) return;
            if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
            expandTimerRef.current = setTimeout(() => {
                setIsExpanded(true);
                // Pin expanded briefly so autoscroll doesn't immediately collapse on mouseleave
                collapseLockRef.current = true;
                wantCollapseRef.current = false;
                if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
                unlockTimerRef.current = setTimeout(() => {
                    collapseLockRef.current = false;
                    const el = cardRef.current;
                    const stillHovered = !!(el && el.matches && el.matches(':hover'));
                    if (!stillHovered && wantCollapseRef.current) {
                        setIsExpanded(false);
                    }
                    wantCollapseRef.current = false;
                }, 1100);
            }, 2000);
        } else {
            if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
            // If expansion lock is active (we auto-scrolled), defer collapse
            if (collapseLockRef.current) {
                wantCollapseRef.current = true;
            } else {
                setIsExpanded(false);
            }
        }
    };

    const handleNavigate = (e) => {
        e.preventDefault();
        navigate(`/anime/${anime.id}`);
    };

    const getDescription = () => {
        if (!anime || anime.description === undefined || anime.description === null) return '';
        return anime.description.length > 80
            ? anime.description.substring(0, 80) + "..."
            : anime.description;
    };

    // Destructure state for cleaner JSX
    const { isHovered, imageLoaded, imageSrc } = state;

    // Only render if we have anime data
    if (!anime) return null;

    return (
        <a
            className={`anime-card ${isExpanded ? 'expanded' : ''}`}
            onClick={handleNavigate}
            onMouseEnter={() => handleCardInteraction(true)}
            onMouseLeave={() => handleCardInteraction(false)}
            ref={cardRef}
            href={`/anime/${anime.id}`}
            style={{ 
                willChange: 'transform, box-shadow',
                transform: isHovered ? 'translateY(5px)' : 'translateY(0)'
            }}
        >
            <div
                className="anime-card-bg"
                style={{ backgroundImage: `url(${imageSrc})` }}
                aria-hidden="true"
            />
            {state.isVisible && (
                <img
                    src={imageSrc}
                    alt={anime.name}
                    loading="lazy"
                    className={`anime-thumbnail ${isHovered && !isExpanded ? 'hovered' : ''} ${imageLoaded ? 'loaded' : ''} ${isExpanded ? 'left' : ''} ${(isExpanded && slideshowReady && slides.length > 0) ? 'dim' : ''}`}
                    style={{ willChange: 'opacity, transform' }}
                />
            )}
            {isExpanded && slideshowReady && slides.length > 0 && (
                <div className="album-slideshow">
                    <div className="album-slideshow-aspect">
                        {layerAUrl && (
                            <img className={`album-slideshow-img ${useLayerA ? 'show' : ''}`} src={layerAUrl} alt="slide-a" />
                        )}
                        {layerBUrl && (
                            <img className={`album-slideshow-img ${!useLayerA ? 'show' : ''}`} src={layerBUrl} alt="slide-b" />
                        )}
                    </div>
                </div>
            )}
            {(!imageLoaded || !state.isVisible) && (
                <div className="anime-card-shimmer" aria-hidden="true" />
            )}
            
            {anime.hq && (
                <img 
                    className={`hq ${isHovered ? 'hovered' : ''}`} 
                    src={diamond} 
                    alt="High quality"
                    loading="lazy" 
                />
            )}
            
            {isInteractive && !isExpanded && (
                <div className="anime-info">
                    <div className='anime-text-container'>
                        <h3 className="anime-title-card">{anime.name}</h3>
                        <p className='anime-description'>{getDescription()}</p>
                    </div>
                    <button 
                        className='button-sec watch-button' 
                        onClick={handleNavigate}
                    >
                        Смотреть
                    </button>
                </div>
            )}

            {isInteractive && (
                <div className="expanded-info">
                    <div className="expanded-text">
                        <h3 className="anime-title-card expanded">{anime.name}</h3>
                        <p className='anime-description expanded'>{getDescription()}</p>
                        <button 
                            className='button-sec watch-button expanded' 
                            onClick={handleNavigate}
                        >
                            Смотреть
                        </button>
                    </div>
                </div>
            )}
           
            {imageLoaded && (
                <div className='badges'>
                    {anime.viral > 10 && (
                        <img className="viral" src={fire} alt="viral" loading="lazy" />
                    )}
                    
                    {anime.rating !== 0 && (
                        <h4 className={`rating ${
                            anime.rating >= 9 ? "premium" : 
                            anime.rating >= 7.5 ? "good" : ""
                        }`}>
                            ★ {anime.rating}
                        </h4>
                    )}
                </div>
            )}
        </a>
    );
});

export default AnimeCardComponent;
